import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBrand } from "@/context/BrandContext";
import { BRANDS, brandPath, brandUrl } from "@/lib/brands";
import { Recycle, ArrowRight, Phone, Menu, X } from "lucide-react";

const NavLink = ({ href, children }) => (
  <a
    href={href}
    className="text-xs font-mono uppercase tracking-[0.2em] text-black/70 hover:text-black transition-colors"
    data-testid={`nav-link-${href.replace("#", "")}`}
  >
    {children}
  </a>
);

const Header = () => {
  const { brand, sister } = useBrand();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const switchBrand = (slug) => {
    const url = brandUrl(slug);
    if (url.startsWith("http")) {
      window.location.href = url;
    } else {
      navigate(url);
    }
    setOpen(false);
  };

  return (
    <header
      className="sticky top-0 z-40 border-b border-black/10 bg-white/80 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="container-x flex items-center justify-between h-[72px] gap-6">
        <Link
          to={brandPath(brand.slug)}
          className="flex items-center gap-3 group"
          data-testid="brand-home-link"
        >
          <div className="w-10 h-10 bg-black text-white grid place-items-center">
            <Recycle size={18} strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display text-[15px] uppercase">
              {brand.short_name}
            </div>
            <div className="eyebrow text-[9px] text-black/60">
              {brand.city}, {brand.state}
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <NavLink href="#materials">Materials</NavLink>
          <NavLink href="#services">Services</NavLink>
          <NavLink href="#capabilities">Capabilities</NavLink>
          <NavLink href="#about">About</NavLink>
          <NavLink href="#prison-reform">Second Chance</NavLink>
          <NavLink href="#locations">Locations</NavLink>
          <NavLink href="#contact">Contact</NavLink>
        </nav>

        {/* Location Switcher */}
        <div className="hidden md:flex items-center gap-3">
          <div
            className="flex items-center border border-black/80 p-1 bg-white"
            data-testid="location-switcher"
          >
            {Object.values(BRANDS).map((b) => (
              <button
                key={b.slug}
                onClick={() => switchBrand(b.slug)}
                className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest transition-colors ${
                  brand.slug === b.slug
                    ? "bg-black text-white"
                    : "text-black/70 hover:text-black"
                }`}
                data-testid={`switch-to-${b.slug}`}
              >
                {b.slug === "qcm" ? "Charlotte" : "Hudson"}
              </button>
            ))}
          </div>
          <a
            href={`tel:${brand.phone.replace(/[^0-9]/g, "")}`}
            className="hard-btn-primary"
            data-testid="header-call-btn"
          >
            <Phone size={14} /> {brand.phone}
          </a>
        </div>

        <button
          className="lg:hidden w-10 h-10 grid place-items-center border border-black/20"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-black/10 bg-white"
          data-testid="mobile-menu-panel"
        >
          <div className="container-x py-6 flex flex-col gap-4">
            <a href="#materials" onClick={() => setOpen(false)} className="py-2 eyebrow">Materials</a>
            <a href="#services" onClick={() => setOpen(false)} className="py-2 eyebrow">Services</a>
            <a href="#capabilities" onClick={() => setOpen(false)} className="py-2 eyebrow">Capabilities</a>
            <a href="#about" onClick={() => setOpen(false)} className="py-2 eyebrow">About</a>
            <a href="#prison-reform" onClick={() => setOpen(false)} className="py-2 eyebrow">Second Chance</a>
            <a href="#locations" onClick={() => setOpen(false)} className="py-2 eyebrow">Locations</a>
            <a href="#contact" onClick={() => setOpen(false)} className="py-2 eyebrow">Contact</a>
            <div className="flex items-center gap-2 pt-2 border-t border-black/10">
              {Object.values(BRANDS).map((b) => (
                <button
                  key={b.slug}
                  onClick={() => switchBrand(b.slug)}
                  className={`flex-1 px-3 py-2 text-xs font-mono uppercase border ${
                    brand.slug === b.slug
                      ? "bg-black text-white border-black"
                      : "border-black/20 text-black/70"
                  }`}
                  data-testid={`mobile-switch-${b.slug}`}
                >
                  {b.short_name}
                </button>
              ))}
            </div>
            <a
              href={`tel:${brand.phone.replace(/[^0-9]/g, "")}`}
              className="hard-btn-primary justify-center"
              data-testid="mobile-call-btn"
            >
              <Phone size={14} /> {brand.phone}
            </a>
            <a
              href={brandUrl(sister.slug)}
              onClick={() => setOpen(false)}
              className="text-xs font-mono uppercase tracking-widest text-[color:var(--accent)] inline-flex items-center gap-2"
            >
              Visit Sister Yard: {sister.short_name} <ArrowRight size={14} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
